import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Badge from "@mui/material/Badge";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import { useDispatch } from "react-redux";
import { getSearch } from "redux/searchSlice";
import { getAuthToken, getAuthUserId } from "utils/authStorage";
import apiService from "services/api";

const formatRelativeTime = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  return date.toLocaleDateString();
};

const profileImageUrl = (profileImage) => {
  if (!profileImage) return null;
  const base = process.env.REACT_APP_IMG || "";
  if (profileImage.startsWith("http")) return profileImage;
  return `${base.replace(/\/$/, "")}/${String(profileImage).replace(/^\//, "")}`;
};

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const dispatcho = useDispatch();
  const token = getAuthToken();
  const userId = getAuthUserId();

  const fetchNotifications = useCallback(async () => {
    if (!token || !userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    try {
      setNotifLoading(true);
      const res = await apiService.getUserNotifications(userId);
      const data = res?.data;
      if (data?.success) {
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
        setUnreadCount(Number(data.unreadCount) || 0);
      }
    } catch (_) {
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setNotifLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  const handleOpenMenu = async (event) => {
    setOpenMenu(event.currentTarget);
    if (userId && unreadCount > 0) {
      try {
        await apiService.markNotificationsSeen(userId);
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, isSeen: true })));
      } catch (_) {
        /* silent */
      }
    }
  };

  const handleCloseMenu = () => setOpenMenu(false);

  const renderNotificationAvatar = (item) => {
    const src = profileImageUrl(item.profileImage);
    if (src) {
      return (
        <Avatar src={src} alt={item.name} sx={{ width: "100%", height: "100%" }} />
      );
    }
    const initial = (item.name || "?").charAt(0).toUpperCase();
    return (
      <SoftTypography variant="button" color="white" fontWeight="bold">
        {initial}
      </SoftTypography>
    );
  };

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
      PaperProps={{ sx: { maxWidth: 360, maxHeight: 420 } }}
    >
      <SoftBox px={2} py={1} borderBottom={1} borderColor="divider">
        <SoftTypography variant="button" fontWeight="bold">
          Notifications
        </SoftTypography>
        {notifications.length > 0 && (
          <SoftTypography variant="caption" color="text" display="block">
            {notifications.length} conversation{notifications.length !== 1 ? "s" : ""}
          </SoftTypography>
        )}
      </SoftBox>

      {notifLoading ? (
        <SoftBox display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </SoftBox>
      ) : notifications.length === 0 ? (
        <SoftBox px={2} py={2}>
          <SoftTypography variant="caption" color="text">
            No notifications yet
          </SoftTypography>
        </SoftBox>
      ) : (
        notifications.map((item) => {
          const titleLine =
            item.notificationText ||
            (item.type === "single_chat"
              ? `${item.name || "Someone"} sent a message`
              : "New notification");
          const preview = item.lastMessage || "";
          return (
            <NotificationItem
              key={item.notificationId || item.chatId}
              image={
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: item.isSeen ? "grey.400" : "info.main",
                    borderRadius: "inherit",
                  }}
                >
                  {renderNotificationAvatar(item)}
                </Box>
              }
              color={item.isSeen ? "secondary" : "info"}
              title={[titleLine, preview ? `— ${preview}` : ""]}
              date={formatRelativeTime(item.createdAt)}
              onClick={handleCloseMenu}
            />
          );
        })
      )}
    </Menu>
  );

  const onChangeInput = (e) => {
    dispatcho(getSearch(e.target.value));
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </SoftBox>
        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            <SoftBox pr={1}>
              <SoftInput
                placeholder="Type here..."
                icon={{ component: "search", direction: "left" }}
                onChange={onChangeInput}
              />
            </SoftBox>
            <SoftBox color={light ? "white" : "inherit"}>
              <Link to={token ? "/profile" : "/login"}>
                <IconButton sx={navbarIconButton} size="small">
                  <Icon
                    sx={({ palette: { dark, white } }) => ({
                      color: light ? white.main : dark.main,
                    })}
                  >
                    account_circle
                  </Icon>
                  {token ? (
                    <SoftTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                      Profile
                    </SoftTypography>
                  ) : (
                    <SoftTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                      Sign in
                    </SoftTypography>
                  )}
                </IconButton>
              </Link>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon classNameName={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <Icon classNameName={light ? "text-white" : "text-dark"}>notifications</Icon>
                </Badge>
              </IconButton>
              {renderMenu()}
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
